import { useTranslation } from "react-i18next";
import { Lightning } from "@phosphor-icons/react";
import { CurrencyInput } from "@/components/ui/currency-input";
import { ClientPriceCalculator } from "@/components/pricing/ClientPriceCalculator";
import { usePricingConfig } from "@/hooks/usePricingConfig";
import type { ServiceSavedBaseline } from "@/lib/service-price-save";
import { cn } from "@/lib/utils";

interface Props {
  enabled: boolean;
  price: number | null;
  onEnabledChange: (v: boolean) => void;
  onPriceChange: (v: number | null) => void;
  /** Spremljena cijena (bez PDV-a + sirovi bruto), da kalkulator za netaknutu cijenu pokaže spremljeni bruto. */
  priceBaseline?: ServiceSavedBaseline;
}

export function EmergencySettingsCard({
  enabled,
  price,
  onEnabledChange,
  onPriceChange,
  priceBaseline,
}: Props) {
  const { t } = useTranslation();
  const pricingConfig = usePricingConfig();
  const priceError = enabled && price !== null && price <= 0;

  return (
    <div className="space-y-4">
      {/* Primary decision: the whole card is the switch for a large, obvious target. */}
      <button
        type="button"
        role="switch"
        aria-checked={enabled}
        aria-label={t("emergency.settings.enabled")}
        onClick={() => onEnabledChange(!enabled)}
        className={cn(
          "flex w-full items-center gap-4 rounded-2xl border p-4 text-left transition-all sm:p-5",
          enabled
            ? "border-primary/30 bg-primary/[0.06] shadow-sm"
            : "border-border/70 bg-card hover:border-primary/30 hover:bg-primary/[0.02]",
        )}
      >
        <span
          className={cn(
            "grid size-11 shrink-0 place-items-center rounded-xl transition-colors",
            enabled ? "bg-primary text-primary-foreground" : "bg-primary/10 text-primary",
          )}
        >
          <Lightning weight="duotone" className="size-6" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-foreground">{t("emergency.settings.enabled")}</p>
          <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{t("emergency.settings.description")}</p>
        </div>
        <span
          className={cn(
            "relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors",
            enabled ? "bg-primary" : "bg-muted-foreground/25",
          )}
        >
          <span
            className={cn(
              "inline-block size-5 rounded-full bg-white shadow-sm transition-transform",
              enabled ? "translate-x-5" : "translate-x-0.5",
            )}
          />
        </span>
      </button>

      {/* Configuration: visually muted and inert until the offer is enabled. */}
      <div
        aria-hidden={!enabled}
        className={cn(
          "space-y-5 rounded-2xl border border-border/70 bg-card p-4 transition-opacity duration-200 sm:p-5",
          !enabled && "pointer-events-none select-none opacity-55",
        )}
      >
        <div>
          <label htmlFor="emergency-price" className="text-sm font-medium text-foreground">
            Cijena usluge (bez PDV-a)
          </label>
          <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{t("emergency.settings.priceHint")}</p>
          <div className="mt-2.5 w-full sm:w-56">
            <CurrencyInput id="emergency-price" suffix="BAM" value={price} onChange={onPriceChange} disabled={!enabled} />
          </div>
          {priceError && <p className="mt-1.5 text-xs text-destructive">{t("emergency.settings.errorPriceRequired")}</p>}
        </div>

        <ClientPriceCalculator amount={price} pricing={pricingConfig} currency="BAM" baseline={priceBaseline} />
      </div>
    </div>
  );
}
